import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Location } from 'react-router-dom';
import { Toaster } from 'sonner';

// Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RISearchProvider } from './contexts/RISearchContext';
import { RISViewerProvider } from './contexts/RISViewerContext';
import { ResearchComparisonProvider } from './contexts/ResearchComparisonContext';
import { authenticatedLandingPath, comparisonWorkspaceEnabled, showsComparisonRoute } from './config/researchLanding';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import RISAssistant from './pages/RISAssistant';
import ResearchComparison from './pages/ResearchComparison';
import SSOLanding from './components/SSOLanding';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import ResearchAccess from './components/ResearchAccess';
import ErrorBoundary from './components/ErrorBoundary';
import OperatorViewBanner from './administration/operator/OperatorViewBanner';
import {BrandProvider} from './contexts/BrandContext';
import { alteAdminAdresse, einstellungsZiel, persoenlicheAdresse } from './pages/einstellungen/seiten';
import { EinstellungenKontext } from './pages/einstellungen/EinstLink';
import { DARSTELLUNG_ANKER } from './pages/einstellungen/praeferenzen';
import { bereichVon, ebenenOrte, HerkunftKontext, istChatOrt } from './components/bereichsEbene';
import { startInVerwaltung } from './pages/accessSummary';
// AE-1: Einstellungen mit Bereichen, seit Nachtrag 3 als Pop-up über der aktuellen Ansicht. Plan 1a Welle 2 (GB-01, G-08):
// der Rahmen ist fest eingebunden und steht mit dem Klick; nur der Inhalt der Bereiche lädt nach. Profil-Nachbau F-02 (D1):
// „Meine Einstellungen“ ist eine eigene Seite (KontoSeite), ebenso fest eingebunden.
import Einstellungen from './pages/einstellungen/Einstellungen';
import KontoSeite from './pages/einstellungen/KontoSeite';
// Profil-Nachbau F-08 (PF-506): das Firmen-Cockpit ist eine Bereichsseite, ebenso fest eingebunden (GB-01). Mit Endung: ohne
// sie findet ein Windows-Dateisystem zuerst firmenCockpit.ts (gleicher Name bis auf die Großschreibung, wie Praeferenzen.tsx).
import FirmenCockpit from './administration/cockpit/FirmenCockpit.tsx';
// Profil-Nachbau F-11 (PF-703): „Einstellungen der Firma“ ist eine Bereichsseite, ebenso fest eingebunden (GB-01); ihre
// Unterrouten stehen in administration/organization/firmenRouten.tsx.
import FirmenSeite from './administration/organization/FirmenSeite';
// Profil-Nachbau F-06/F-19, BAU-HILFE.md 1.1/1.2 (PF-308-2): „Hilfe und Feedback“ ist eine Bereichsseite, ebenso fest eingebunden
// (GB-01); dazu die Umleitungen der alten Adressen.
import HilfeSeite from './components/hilfe/HilfeSeite';
import { EinstellungenHilfeUmleitung, HilfeUmleitung, UeberUmleitung } from './components/hilfe/HilfeUmleitung';
import { Ersatz, ladeErsatz, LadeRoute } from './pages/einstellungen/ladeErsatz';
import { vorladenImLeerlauf } from './pages/einstellungen/vorladen';

const Administration = lazy(() => import('./administration/Administration'));
const AcceptInvitation = lazy(() => import('./administration/AcceptInvitation'));
const AccountAccess = lazy(() => import('./pages/AccountAccess'));
const AccountBilling = lazy(() => import('./pages/AccountBilling'));
const Register = lazy(() => import('./pages/Register'));
const TarifWaehlen = lazy(() => import('./pages/TarifWaehlen'));
// Paket 08 Schritt 10 (EIN-20): Bestätigung des E-Mail-Wechsels, öffentlich wie die Passwortseiten.
const ConfirmEmailChange = lazy(() => import('./pages/ConfirmEmailChange'));
const Sicherheit = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.Sicherheit })));
const MeineDaten = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.MeineDaten })));
const SichtWechseln = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.SichtWechseln })));
const Profil = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.Profil })));
const Nutzung = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.Nutzung })));
const Tarife = lazy(() => import('./pages/einstellungen/Unterseiten').then(m => ({ default: m.Tarife })));
const ZweiFaktorPflicht = lazy(() => import('./pages/ZweiFaktorPflicht'));
// Profil-Nachbau F-17 (PF-787, BAU-RECHT.md 3.2): die Zustimmungsseite der Nutzungsrichtlinie, wie die Zwei-Faktor-Pflicht.
const RichtlinieZustimmung = lazy(() => import('./pages/RichtlinieZustimmung'));
// Profil-Nachbau F-20 (PF-787, BAU-RECHT.md 4.4): die öffentliche Seite der Rechtstexte unter /rechtliches/:slug.
const Rechtstext = lazy(() => import('./pages/Rechtstext'));
// Profil-Nachbau F-09 (PF-205-2). Mit Endung: ohne sie findet ein Windows-Dateisystem zuerst praeferenzen.ts (gleicher Name
// bis auf die Großschreibung; Vorbild Nutzungsbalken.tsx).
const Praeferenzen = lazy(() => import('./pages/einstellungen/Praeferenzen.tsx'));
// Profil-Nachbau F-10 (PF-606-2). Mit Endung wie Praeferenzen: daneben liegt benachrichtigungen.ts.
const Benachrichtigungen = lazy(() => import('./pages/einstellungen/Benachrichtigungen.tsx'));
// Profil-Nachbau F-20.3 (BAU-VORLAGEN.md 5.4): Vorlagen für Rechtsfragen, eine App-Seite mit der App-Leiste.
const Vorlagen = lazy(() => import('./pages/vorlagen/Vorlagen'));
// Profil-Nachbau F-14 (PF-806, BAU-TEAMS.md 1.7): „Mein Team“ in „Meine Einstellungen“.
const MeinTeam = lazy(() => import('./administration/teams/MeinTeam'));
// Der Ersatz außerhalb des Pop-ups; data-laden ist die Marke, auf die Prüfungen warten (GB-01).
const loadingPage = <p role="status" aria-busy="true" data-laden className="p-8 text-primary">Seite wird geladen …</p>;
// Plan 1a Welle 2 (GB-01, G-08): der Ersatz beim Nachladen einer Unterseite hat den Seitenkopf des Ziels und sagt, was lädt.
// Titel und Ladetext je Route nennt ladeErsatz (pages/einstellungen/ladeErsatz.tsx).
const laden = (element: React.ReactNode, titel: string, text?: string) => <Suspense fallback={<Ersatz titel={titel} text={text} />}>{element}</Suspense>;
const seite = (element: React.ReactNode, route: LadeRoute) => { const { titel, text } = ladeErsatz(route); return laden(element, titel, text); };
// AE-10: die alte Adresse der Admin-Einstellungen ersetzt sich still durch die Unterseite; Anker bleibt.
function AdminUmleitung() {
  const { search, hash } = useLocation();
  return <Navigate to={alteAdminAdresse(search) + hash} replace />;
}
// AE-10: die früheren persönlichen Seiten leiten auf ihre Unterseite; Abfrage (etwa die Rückkehr vom Zahlungsdienst) und
// Anker bleiben.
function Umleitung({ nach }: { nach: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={nach + search + hash} replace />;
}
// Plan 1a Welle 1, G3-6 (MA-07, W1-O02, AUFTEILUNG 5.5): frühere Bereiche sind jetzt Abschnitte; ihre alte Adresse im Pop-up
// öffnet den neuen Bereich am Abschnitt (Anker). Abfrage, eigener Anker und der Hintergrund des Pop-ups bleiben.
function AbschnittUmleitung({ nach, anker }: { nach: string; anker: string }) {
  const { search, hash, state } = useLocation();
  return <Navigate to={`${nach}${search}${hash || `#${anker}`}`} replace state={state} />;
}
// Profil-Nachbau F-03 (PF-201, BAU-SEITENLISTE.md 1.1): eine umbenannte Seite der Konto-Ebene; Abfrage, eigener Anker und Herkunft
// (state.hintergrund) bleiben, ohne Vorgabe-Anker. Umleitung gibt den Zustand nicht mit.
function SeitenUmleitung({ nach }: { nach: string }) {
  const { search, hash, state } = useLocation();
  return <Navigate to={nach + search + hash} replace state={state} />;
}

/**
 * Die Vergleichsseite gehört den Entwicklerkonten. Ein gewöhnliches Konto --
 * und ebenso eines, dessen Fähigkeiten noch nicht feststehen -- landet ohne
 * tote Seite im Chat.
 */
export const DeveloperComparisonRoute: React.FC = () => {
  const { isDeveloper } = useAuth();
  return showsComparisonRoute(isDeveloper) ? <ResearchComparison /> : <Navigate to="/ris" replace />;
};

// Bewertung Befund 8 (AE-F6): /abrechnung (auch die Rückkehr vom Zahlungsdienst und vom Zahlungsportal) führt einen Admin,
// der die Abrechnung seiner Firma öffnen darf, in deren Bereich der Admin-Einstellungen; sonst beim Einzelkonto in
// „Abrechnung und Kosten“, bei jedem anderen Konto über AccountBilling zu „Nutzung“ (1A-1, Bennys Antwort 09.10.2026).
function AbrechnungUmleitung() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Erste Zahlung nur mit canPay, auch bei offenem Tarifauftrag; Leser behalten das normale Abrechnungsziel.
  if (user?.account?.canPay && user.access?.organization?.status === 'PENDING_PAYMENT' && !user.sicht?.aktiv) return <Umleitung nach="/tarif-waehlen" />;
  return <Umleitung nach={einstellungsZiel('/abrechnung', user?.ebenen, { organisationId: user?.access?.organization?.id, canOpenBilling: user?.account?.canOpenBilling }) ?? '/einstellungen/abrechnung'} />;
}

// AE-1 Nachtrag 3 (SPEC AE-01): jede Adresse unter /einstellungen zeigt das Pop-up über einem Hintergrund. Hintergrund
// ist die Ansicht, aus der es geöffnet wurde (Verlaufszustand hintergrund), sonst die Startansicht. Er bleibt gerendert
// und behält seinen Zustand, ist während der Anzeige aber inert; das Band einer Sicht liegt außerhalb.
// Profil-Nachbau F-02 (D1), B1.2 und B9.1: drei Ebenen – App, darüber eine Bereichsseite (zuerst „Meine Einstellungen“),
// darüber das Pop-up. Den Ort jeder Ebene nennt ebenenOrte (components/bereichsEbene.ts).
// UI-05/UI-14: die Startansicht ist das Ziel nach der Anmeldung (die Startseite, für jedes Konto); im Hintergrund leitet
// nichts um, das die Adresse des Pop-ups ersetzen könnte.
const START: Location = { pathname: authenticatedLandingPath(), search: '', hash: '', state: null, key: 'einstellungen-start' };
function AppRoutes() {
  const location = useLocation();
  const { user, isAuthenticated, loading, zweiFaktorPflicht, richtliniePflicht } = useAuth();
  // PF-403-3 (F-19, „Die Herkunft merkt sich die App beim Öffnen des Bereichs“): der letzte App-Ort. Ohne state.hintergrund
  // (ein Link ohne Zustand wie der Menüeintrag „Plattform-Verwaltung“, ein Seitenwechsel über die Abfrage) bleibt er die App
  // hinter der Ebene; ein echter Hintergrund geht vor (ebenenOrte). Gemerkt wird erst nach der Anmeldeprüfung und nach den
  // Effekten der Seite: Leitet der App-Ort sofort weiter (/zugang, /abrechnung, die Startseite eines Kontos, das nie recherchiert), stimmt
  // die Adresse des Browsers dann nicht mehr mit ihm überein, und er zählt nicht; sonst führte „Zurück zum Chat“ wieder in die
  // Weiterleitung.
  const letzteApp = useRef<Location | null>(null);
  const orte = ebenenOrte(location, letzteApp.current ?? START);
  const offen = orte.ebene !== 'app';
  useEffect(() => {
    // PF-SL1-verwaltung-1 (Schlusslauf 1, system-ui): abgemeldet gibt es keinen App-Ort. Sonst blieb die Anmeldeseite (mit
    // state.from) gemerkt und stand nach der Anmeldung hinter einer Bereichsseite; angemeldet leitet sie auf from weiter, etwa auf
    // /verwaltung?organisation=…&bereich=roles, das einen Firmenadmin wieder in seine Bereichsseite schickt – eine Schleife.
    if (!loading && !isAuthenticated) { letzteApp.current = null; return; }
    // F-23 (BEFUNDE-FEHLERSUCHE-20261015 Punkt 3): gemerkt wird nur ein Ort mit der App-Leiste (istChatOrt), nie eine Anmelde-,
    // Bestätigungs- oder Rechtstextseite.
    if (!loading && orte.ebene === 'app' && istChatOrt(location.pathname) && window.location.pathname + window.location.search === location.pathname + location.search) letzteApp.current = location;
  }, [location, loading, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  // PF-403-3 (D-23), Profil-Nachbau F-22: hinter der Plattform-Verwaltung steht keine App, solange der Zugang fehlt (noch nicht
  // geladen) oder die Startseite des Kontos in die Verwaltung führt (startInVerwaltung, etwa Support); dort stünde sonst eine
  // Startseite, die wieder hierher leitet, und AppLayout lüde Daten eines Recherchekontos. Ein Betreiberkonto mit
  // product.research.operate ohne Platz hat die App dahinter: es sieht die Startseite des Chats mit der Sperrkarte.
  const ohneApp = !!orte.bereich && bereichVon(orte.bereich.pathname) === 'verwaltung' && (!user?.access || startInVerwaltung(user.access, user.ebenen, !!user.sicht?.aktiv));
  const huelle = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { huelle.current?.toggleAttribute('inert', offen); }, [offen, ohneApp, loading, isAuthenticated, zweiFaktorPflicht, richtliniePflicht]);
  // Plan 1a Welle 2 (GB-01, G-08): angemeldet lädt der Browser die Unterseiten der Einstellungen im Leerlauf vor, einmal je
  // Sitzung; das Kontomenü stößt dasselbe beim Öffnen an (components/UserMenu.tsx).
  useEffect(() => (isAuthenticated ? vorladenImLeerlauf() : undefined), [isAuthenticated]);
  // Paket AA (Befund 6): verlangt die Firma Zwei-Faktor und fehlt sie, gibt es nur die Einrichtung (Server: TWO_FACTOR_SETUP_REQUIRED).
  if (zweiFaktorPflicht) return <Suspense fallback={loadingPage}><ZweiFaktorPflicht /></Suspense>;
  // Profil-Nachbau F-17 (PF-787, BAU-RECHT.md 3.2): hat die Firma eine neue Fassung der Nutzungsrichtlinie, gibt es nur die
  // Zustimmung (Server: USAGE_POLICY_REQUIRED). Die Rechtstexte unter /rechtliches/ bleiben lesbar (F-20, BAU-RECHT.md 4.4).
  if (richtliniePflicht && !location.pathname.startsWith('/rechtliches/')) return <Suspense fallback={loadingPage}><RichtlinieZustimmung /></Suspense>;
  if (offen && loading) return null;
  if (offen && !isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  // PF-403-3: HerkunftKontext nennt den App-Ort, der hinter der Ebene wirklich steht; „Zurück zum Chat“ führt dorthin.
  return (
    <HerkunftKontext.Provider value={orte.app}>
    <EinstellungenKontext.Provider value={{ offen, ort: location }}>
      {/* Profil-Nachbau F-06/F-19, BAU-HILFE.md 1.2 (PF-308-2): ?hilfe=… auf jeder angemeldeten Seite ersetzt sich durch die
          Seite der Hilfe (replace); das Ziel trägt keinen hilfe-Parameter mehr, also keine Schleife. */}
      {isAuthenticated && <HilfeUmleitung />}
      {!ohneApp && <div ref={huelle} style={{ display: 'contents' }}>
            <Routes location={orte.app}>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/e-mail-bestaetigen" element={<Suspense fallback={loadingPage}><ConfirmEmailChange /></Suspense>} />
              <Route path="/einladung" element={<Suspense fallback={loadingPage}><AcceptInvitation /></Suspense>} />
              <Route path="/registrieren" element={<Suspense fallback={loadingPage}><Register /></Suspense>} />
              {/* Profil-Nachbau F-20 (PF-787, BAU-RECHT.md 4.4): Rechtstexte ohne Anmeldung, auch angemeldet und während der
                  Pflicht der Nutzungsrichtlinie. */}
              <Route path="/rechtliches/:slug" element={<Suspense fallback={loadingPage}><Rechtstext /></Suspense>} />
              {/* Drei Ebenen (SPEC VE-06, E-VE-9): die Admin-Einstellungen stehen seit AE-1 unter /einstellungen/organisation
                  im App-Rahmen, die alte Adresse leitet um. Die Verwaltung steht seit PF-403-3 in der Bereichs-Ebene (unten). */}
              <Route path="/admin-einstellungen" element={<ProtectedRoute requiredAccess="firma"><AdminUmleitung /></ProtectedRoute>} />
              <Route path="/zugang" element={<Umleitung nach="/einstellungen/zugang" />} />
              <Route path="/abrechnung" element={<AbrechnungUmleitung />} />
              <Route path="/nachrichten" element={<Umleitung nach="/einstellungen/benachrichtigungen" />} />

              {/* SSO entry point */}
              <Route path="/sso" element={<SSOLanding />} />

              {/* Protected routes with persistent AppLayout */}
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path="/" element={<ResearchAccess><Dashboard /></ResearchAccess>} />
                <Route path="/ris" element={<ResearchAccess><RISAssistant /></ResearchAccess>} />
                <Route path="/tarif-waehlen" element={<Suspense fallback={loadingPage}><TarifWaehlen /></Suspense>} />
                {/* Profil-Nachbau F-20.3 (BAU-VORLAGEN.md 5.4): Vorlagen für Rechtsfragen, ohne ResearchAccess (Vorlagen pflegen geht
                    auch ohne Recherchezugang). */}
                <Route path="/vorlagen" element={<Suspense fallback={loadingPage}><Vorlagen /></Suspense>} />
                {comparisonWorkspaceEnabled && (
                  <Route path="/ris/compare" element={<ResearchAccess><DeveloperComparisonRoute /></ResearchAccess>} />
                )}
              </Route>

              {/* Legacy redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
      </div>}
      {orte.bereich && <Routes location={orte.bereich}>
        {/* Profil-Nachbau F-02 (D1), B1.2: „Meine Einstellungen“ ist eine Bereichsseite über der App; weitere Bereiche hängen
            hier ihre Adressen an. Liegt ein Pop-up darüber, bleibt dieser Block an derselben Stelle gerendert und die Seite mit
            ihren Eingaben gemountet. */}
        <Route path="/einstellungen" element={<KontoSeite unten={orte.ebene === 'popup'} />}>
          <Route index element={seite(<Profil />, 'profil')} />
          <Route path="profil" element={seite(<Profil />, 'profil')} />
          <Route path="zugang" element={seite(<AccountAccess />, 'zugang')} />
          <Route path="mein-team" element={seite(<MeinTeam />, 'mein-team')} />
          <Route path="nutzung" element={seite(<Nutzung />, 'nutzung')} />
          {/* PF-201 (BAU-SEITENLISTE.md 1, 1.1): „Benachrichtigungen“ ist eine Seite der Konto-Ebene, seit F-10 (PF-606-2) die
              Seite mit Posteingang und „E-Mail je Ereignis“; die alte Adresse „nachrichten“ führt dorthin. */}
          <Route path="benachrichtigungen" element={seite(<Benachrichtigungen />, 'benachrichtigungen')} />
          <Route path="nachrichten" element={<SeitenUmleitung nach={persoenlicheAdresse('benachrichtigungen')} />} />
          <Route path="abrechnung" element={seite(<AccountBilling />, 'abrechnung')} />
          <Route path="tarife" element={seite(<Tarife />, 'tarife')} />
          <Route path="sicherheit" element={seite(<Sicherheit />, 'sicherheit')} />
          <Route path="praeferenzen" element={seite(<Praeferenzen />, 'praeferenzen')} />
          {/* BAU-SEITENLISTE 1.1: die alte Adresse öffnet „Präferenzen“ am Abschnitt „Darstellung“; Abfrage, eigener Anker und
              Herkunft bleiben. */}
          <Route path="darstellung" element={<AbschnittUmleitung nach={persoenlicheAdresse('praeferenzen')} anker={DARSTELLUNG_ANKER} />} />
          <Route path="daten" element={seite(<MeineDaten />, 'daten')} />
          {/* F-23 (Fehlersuche Nr. 8, F-19): „Sicht wechseln“ als Seite von „Meine Einstellungen“ (seiten.ts PERSOENLICHE_SEITEN). */}
          <Route path="sicht" element={seite(<SichtWechseln />, 'sicht')} />
        </Route>
        {/* Profil-Nachbau F-11 (PF-703): Einstellungen der Firma als Bereichsseite; Unterrouten in
            administration/organization/firmenRouten.tsx. */}
        <Route path="/einstellungen/organisation/*" element={<ProtectedRoute requiredAccess="firma"><FirmenSeite unten={orte.ebene === 'popup'} /></ProtectedRoute>} />
        {/* PF-403-3 (F-19, F-20): die Plattform-Verwaltung ist eine Bereichsseite wie „Meine Einstellungen“; die Unterpfade
            braucht PF-409. Ohne Recherchezugang steht keine App dahinter (ohneApp oben). */}
        <Route path="/verwaltung/*" element={<ProtectedRoute requiredAccess="plattform"><Suspense fallback={loadingPage}><Administration key="plattform" ebene="plattform" unten={orte.ebene === 'popup'} /></Suspense></ProtectedRoute>} />
        {/* PF-506 (F-08, F-19): das KI-Cockpit der eigenen Firma als Bereichsseite. */}
        <Route path="/ki-cockpit" element={<ProtectedRoute requiredAccess="firma"><FirmenCockpit unten={orte.ebene === 'popup'} /></ProtectedRoute>} />
        {/* Profil-Nachbau F-06/F-19, BAU-HILFE.md 1.1 (PF-308-2): „Hilfe und Feedback“ als Bereichsseite für jedes angemeldete
            Konto (offen && !isAuthenticated führt oben zur Anmeldung); /hilfe ohne Unterseite ersetzt HilfeSeite durch
            /hilfe/dokumente. */}
        <Route path="/hilfe/*" element={<HilfeSeite unten={orte.ebene === 'popup'} />} />
      </Routes>}
      {orte.ebene === 'popup' && <Routes>
        {/* F-23 (Fehlersuche Nr. 8, F-19): kein Bereich öffnet mehr das alte Fenster. „Über …“ steht auf /hilfe/dokumente
            (UEBER_ZIEL), die frühere Hilfe ebenfalls; beide leiten um, bevor das Fenster rendert. „Sicht wechseln“ ist eine Seite
            von „Meine Einstellungen“ (Bereichs-Block oben). Das Fenster bleibt nur für unbekannte Adressen und leitet um. */}
        <Route path="/einstellungen/ueber" element={<UeberUmleitung />} />
        <Route path="/einstellungen/hilfe" element={<EinstellungenHilfeUmleitung />} />
        <Route path="/einstellungen" element={<Einstellungen />}>
          <Route path="*" element={<Navigate to="/einstellungen" replace />} />
        </Route>
      </Routes>}
    </EinstellungenKontext.Provider>
    </HerkunftKontext.Provider>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrandProvider>
      <RISearchProvider>
        <RISViewerProvider>
          <Router>
            <OperatorViewBanner />
            <ResearchComparisonProvider>
              <AppRoutes />
            </ResearchComparisonProvider>
          </Router>
          {/* UI-17: unter dem Kopf der Ansicht (76 px), damit keine Meldung dessen Bedienelemente verdeckt. F-23 (Fehlersuche
              Nr. 27): Vorlesename deutsch; „Benachrichtigungen“ heißt die Glocke. */}
          <Toaster position="top-right" offset={{ top: 88 }} mobileOffset={{ top: 88 }} richColors containerAriaLabel="Meldungen" />
        </RISViewerProvider>
      </RISearchProvider>
      </BrandProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
